import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, ShoppingBag, PlusCircle, Users, Settings,
  LogOut, Package, Circle, ShieldAlert, Menu, X, Star, BellRing, Truck
} from 'lucide-react';
import { useAuth } from "../../context/AuthContext";
import SEO from '../../components/SEO';

const AdminLayout = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();

  // --- Live Badge Logic ---
  useEffect(() => {
    const getCount = async () => {
      try {
        const savedUser = localStorage.getItem("weThriftUser");
        const userData = savedUser ? JSON.parse(savedUser) : null;
        const token = userData?.accessToken; 

        if (!token) return;

        const res = await axios.get("https://wethrift-backend.onrender.com/api/requests/count/pending", {
          headers: { token: `Bearer ${token}` }
        });

        if (res.data) {
          setPendingCount(res.data.count);
        }
      } catch (err) {
        console.error("Badge Fetch Error:", err.response?.data || err.message);
      }
    };

    getCount();
    const interval = setInterval(getCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // --- MENU ITEMS UPDATED ---
 const menuItems = [
  { name: 'Overview', path: '/admin/dashboard', icon: <LayoutDashboard size={20} /> },
  { name: 'Inventory', path: '/admin/products', icon: <Package size={20} /> },
  { name: 'Orders', path: '/admin/orders', icon: <ShoppingBag size={20} /> },
  { name: 'Bulk Requests', path: '/admin/bulk-leads', icon: <Truck size={20} /> }, // NAYA TAB
  { name: 'Add Product', path: '/admin/add-product', icon: <PlusCircle size={20} /> },
  { name: 'Reviews', path: '/admin/reviews', icon: <Star size={20} /> },
  { name: 'Notifications', path: '/admin/notifications', icon: <BellRing size={20} /> },
  { name: 'Requests', path: '/admin/requests', icon: <ShieldAlert size={20} />, badge: true }, 
  { name: 'Users', path: '/admin/users', icon: <Users size={20} /> },
  { name: 'Settings', path: '/admin/settings', icon: <Settings size={20} /> },
];

  return (

    <>
    <SEO 
        title="Admin Control Terminal" 
        description="Confidential Internal Access Only." 
        noIndex={true} 
      />
    <div className="flex h-screen bg-black text-zinc-400 font-sans overflow-hidden">
      
      {/* --- SIDEBAR --- */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-zinc-950 border-r border-zinc-900 flex flex-col shadow-2xl transition-transform duration-300 transform
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:relative lg:translate-x-0
      `}>
        
        {/* Logo Section */}
        <div className="p-8 border-b border-zinc-900/50 flex flex-col items-center relative">
          <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden absolute right-4 top-4 text-zinc-500 hover:text-white"
          >
            <X size={24} />
          </button>
          <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 flex items-center justify-center mb-4 shadow-inner">
              <img src="/we-thrift-logo.png" alt="Logo" className="h-10 w-auto object-contain" crossOrigin="anonymous"/>
          </div>
          <span className="font-black text-xl italic tracking-tighter text-zinc-100 uppercase">
            WE<span className="text-emerald-500">THRIFT</span>
          </span>
          <span className="text-[9px] font-bold text-zinc-600 uppercase tracking-[0.3em] mt-1">Command Center</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-6 space-y-2 mt-4 overflow-y-auto custom-scrollbar">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path === '/admin/dashboard' && location.pathname === '/admin');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-4 px-5 py-3.5 rounded-2xl transition-all duration-300 group ${
                  isActive 
                  ? 'bg-emerald-500 text-black font-black italic shadow-lg shadow-emerald-500/10' 
                  : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100'
                }`}
              >
                <span className={isActive ? 'text-black' : 'text-emerald-500/50 group-hover:text-emerald-500'}>
                  {item.icon}
                </span>
                <span className="text-xs uppercase tracking-widest">{item.name}</span>

                {/* Live Notification Badge */}
                {item.badge && pendingCount > 0 && (
                  <span className={`absolute right-4 px-2 py-0.5 text-[10px] font-bold rounded-full border-2 
                    ${isActive ? 'bg-black text-emerald-500 border-emerald-400' : 'bg-red-600 text-white border-zinc-950 animate-pulse'}`}>
                    {pendingCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Section */}
        <div className="p-6 border-t border-zinc-900">
          <button 
            onClick={logout}
            className="flex items-center gap-4 text-zinc-500 px-5 py-4 w-full hover:bg-red-500/10 hover:text-red-500 rounded-2xl transition-all duration-300 group"
          >
            <LogOut size={20} className="group-hover:rotate-12 transition-transform" />
            <span className="text-xs uppercase font-black tracking-widest text-left">Terminate Session</span>
          </button>
        </div>
      </aside>

      {/* --- MAIN CONTENT AREA --- */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {/* Header */}
        <header className="h-20 bg-zinc-950/50 backdrop-blur-md border-b border-zinc-900 px-4 lg:px-10 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 text-zinc-400 hover:text-emerald-500 bg-zinc-900 rounded-xl"
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
               <span className="hidden sm:inline">Console</span> 
               <span className="hidden sm:inline text-zinc-800">/</span>
               <span className="text-zinc-300 italic truncate max-w-[150px]">
                 {location.pathname.split('/').pop() || 'Dashboard'}
               </span>
            </div>
          </div>

          <div className="flex items-center gap-3 lg:gap-6">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-black text-zinc-100 uppercase italic leading-none">Root_Admin</p>
              <div className="flex items-center justify-end gap-1.5 mt-1">
                <Circle size={6} fill="#10b981" className="text-emerald-500 animate-pulse" />
                <p className="text-[8px] text-emerald-500 font-black uppercase tracking-widest">System Online</p>
              </div>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center font-black text-emerald-500 shadow-inner">
              A
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-10 bg-black scroll-smooth">
          {isMobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
          <div className="max-w-7xl mx-auto">
             <Outlet /> 
          </div>
        </main>
      </div>
    </div>
    </>
  );
};

export default AdminLayout;